/** Downscale + compress an image File to a small JPEG data URL.
 *
 * A profile photo must never be persisted at full resolution: a raw phone photo
 * is 3–10 MB as a base64 data URL, which overflows the ~5 MB localStorage/
 * sessionStorage quota (→ QuotaExceededError, a crash on sign-up) and bloats the
 * profile-sync payload. A 256 px JPEG is ~20–40 KB — safe everywhere and plenty
 * for an avatar. Falls back to the raw data URL only if the canvas path is
 * unavailable (rare); callers still guard persistence against quota.
 */
export async function fileToAvatarDataUrl(
  file: File,
  maxPx = 256,
  quality = 0.82,
): Promise<string | null> {
  const rawUrl = await new Promise<string | null>((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === "string" ? r.result : null);
    r.onerror = () => resolve(null);
    r.readAsDataURL(file);
  });
  if (!rawUrl) return null;
  if (typeof document === "undefined") return rawUrl;

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = rawUrl;
    });
    const scale = Math.min(1, maxPx / Math.max(img.width, img.height || 1));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return rawUrl;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return rawUrl; // decode/canvas failed — persistence layer still guards quota
  }
}
